// 로그인 후 Talent 앱의 내비게이션 구조.
// 핵심 탭 4개(홈/내 커리어/포지션 탐색/취업 소식) + 계정 설정(프로필 메뉴).
import { House, Compass, Briefcase, Newspaper, ClipboardText, Microphone, type Icon } from "@phosphor-icons/react";

export const talentAppRoutes = {
  // 홈(GNB 대시보드)과 랜딩(공개)을 분리. /talent = 랜딩, /talent/home = 홈.
  home: "/talent/home",
  onboarding: "/talent/onboarding",
  career: "/talent/career",
  experiences: "/talent/career/experiences",
  profile: "/talent/career/profile",
  history: "/talent/career/history",
  resume: "/talent/career/resume/editor",
  resumePreview: "/talent/career/resume/preview",
  resumes: "/talent/career/resumes",
  // 이력서 AI 진단(레거시 도구를 리뉴얼 경로로 되살린 화면) — per-id 가 아니다.
  resumeDiagnosis: "/talent/career/resume/diagnosis",
  cover: "/talent/career/cover/editor",
  coverPreview: "/talent/career/cover/preview",
  coverLetters: "/talent/career/cover-letters",
  interviews: "/talent/career/interviews",
  feed: "/talent/feed",
  jobs: "/talent/jobs",
  community: "/talent/community",
  insights: "/talent/insights",
  applications: "/talent/applications",
  connections: "/talent/connections",
  assignments: "/talent/assignments",
  programs: "/talent/programs",
  notifications: "/talent/notifications",
  settings: "/talent/settings"
} as const;

export type TalentTabKey = "feed" | "home" | "career" | "jobs" | "community" | "interviews" | "insights" | "applications";

export interface TalentNavItem {
  key: TalentTabKey;
  label: string;
  href: string;
  icon: Icon;
  // 로그인 없이도 열람 가능한 공개 탭(포지션 탐색·취업 가이드). 미지정은 로그인 필요.
  guest?: boolean;
}

// 하단/상단 공용 메인 탭.
// '나에게 관심 준 회사'는 GNB 탭이 아닌 설정 카드에서 진입(/talent/feed 라우트 재사용).
export const talentMainNav: TalentNavItem[] = [
  { key: "home", label: "홈", href: talentAppRoutes.home, icon: House },
  { key: "career", label: "내 커리어", href: talentAppRoutes.career, icon: Compass },
  { key: "jobs", label: "포지션 탐색", href: talentAppRoutes.jobs, icon: Briefcase, guest: true },
  // 커뮤니티는 GNB 에서만 가렸다(화면·주소는 그대로) — 그 자리에 모의 면접을 둔다.
  { key: "interviews", label: "모의 면접", href: talentAppRoutes.interviews, icon: Microphone },
  { key: "applications", label: "지원 현황", href: talentAppRoutes.applications, icon: ClipboardText },
  { key: "insights", label: "취업 가이드", href: talentAppRoutes.insights, icon: Newspaper, guest: true }
];

// 현재 경로가 해당 탭에 속하는지 판단.
export function isTabActive(pathname: string, href: string): boolean {
  if (href === talentAppRoutes.home) return pathname === href;
  // 모의 면접은 /talent/career 아래 있지만 따로 탭이 있다 — 내 커리어 탭까지 켜지지 않게.
  if (href === talentAppRoutes.career && isTabActive(pathname, talentAppRoutes.interviews)) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
}

// GNB 네비 라벨 다국어 — 데이터의 한국어 label 대신 화면에서 이 훅으로 번역해 노출.
import { usePlatformT } from "../i18n";
export function useTalentNavLabel(): (key: TalentTabKey) => string {
  const t = usePlatformT();
  return (key) => {
    switch (key) {
      case "home":
        return t("홈", "Home", "首页", "Trang chủ", "ホーム", "Beranda");
      case "career":
        return t("내 커리어", "Career", "我的职业", "Sự nghiệp", "キャリア", "Karier");
      case "jobs":
        return t("포지션 탐색", "Jobs", "职位", "Việc làm", "求人", "Lowongan");
      case "community":
        return t("커뮤니티", "Community", "社区", "Cộng đồng", "コミュニティ", "Komunitas");
      case "interviews":
        return t("모의 면접", "Interview", "模拟面试", "Phỏng vấn", "模擬面接", "Wawancara");
      case "applications":
        return t("지원 현황", "Applied", "申请", "Ứng tuyển", "応募", "Lamaran");
      case "insights":
        return t("취업 가이드", "Guide", "指南", "Hướng dẫn", "ガイド", "Panduan");
      case "feed":
        return t("관심 회사", "Interested", "关注公司", "Công ty QT", "関心企業", "Perusahaan");
      default:
        return key;
    }
  };
}
